<template>
    <div v-if="!userCanImportExportMetadata">
        <hox-alert
            type="warning"
            class="importExportModal__howto"
            size="small"
            data-testid="import-export-modal__metadata-no-permission"
        >
            <template #title>You do not have access to this feature</template>
            <template #content>
                <p>
                    You need permission to manage metadata or to change reporting labels in order to import and export
                    them.
                </p>
            </template>
        </hox-alert>
    </div>
    <div v-else-if="isCampaignLocked">
        <hox-alert
            type="warning"
            class="importExportModal__howto"
            size="small"
            data-testid="import-export-modal__metadata-campaign-locked"
        >
            <template #title>Campaign is locked</template>
            <template #content>
                <p>
                    Importing and exporting metadata is unavailable while a task is being processed on this campaign.
                    Check the notification drawer for more details.
                </p>
            </template>
        </hox-alert>
    </div>
    <div v-else>
        <hox-alert type="info" class="importExportModal__howto" size="small">
            <template #title>To update {{ editableColumnsLabel }}</template>
            <template #content>
                <div>
                    <p>
                        1.
                        <span
                            class="link-like"
                            :class="{ 'link-like--disabled': isExportingMetadata }"
                            data-testid="import-export-modal__download-metadata-link"
                            @click="downloadMetadata"
                        >
                            <strong>Download deliverables</strong>
                            <LoadingSpinner v-if="isExportingMetadata" class="importExportModal__download-spinner" />
                        </span>
                        for the current selection
                    </p>
                    <p>2. Fill in the {{ editableColumnsLabel }} in the file</p>
                    <p>3. Update the content by uploading XLSX file below</p>
                </div>
            </template>
        </hox-alert>

        <Alert
            :type="metadataSelectionCount ? 'info' : 'warning'"
            class="importExportModal__number-of-deliverables"
            size="small"
        >
            {{
                metadataSelectionCount === 0
                    ? "No deliverables selected"
                    : `You have selected ${metadataSelectionCount} deliverable${metadataSelectionCount === 1 ? "" : "s"}.`
            }}

            <template v-if="metadataSelectionCount" #desc> These will be included in the downloaded file. </template>
            <template v-else #desc> The file will include all deliverables based on the current filters. </template>
        </Alert>

        <div class="importExportModal__upload-controls">
            <XlsUploadButton @click.stop="resetUpload" @load="onXlsLoad" @error="onImportError" />
        </div>

        <div class="importExportModal__upload-metadata-results">
            <div
                v-if="fileName"
                class="importExportModal__upload-result__file"
                data-testid="import-export-modal__metadata-file-name"
            >
                {{ fileName }} ({{ rows.length }} rows)
            </div>

            <Alert v-if="hasError" type="error" data-testid="import-export-modal__metadata-error">
                <template #desc>
                    <div v-if="errorDetails.length">
                        <p v-for="detail in errorDetails" :key="detail">{{ detail }}</p>
                    </div>
                </template>
            </Alert>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import XlsUploadButton from "@/components/Campaign/XlsUploadButton.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { userCan } from "@/services/Auth";
import {
    METADATA_SHEET_FIXED_HEADERS,
    METADATA_SHEET_NAME,
    type MetadataSheetFixedHeader
} from "@/utils/metadataSheetSchema";

import type {
    ImportedSheet,
    ImportedXlsData,
    ImportRow,
    MetadataImportRow,
    MetadataTabState,
    XlsUploadError
} from "../../../types/importExportTypes";
import { useDeliverableImport } from "./useDeliverableImport";

interface MetadataTabData {
    fileName: string;
    hasError: boolean;
    errorDetails: string[];
    rows: MetadataImportRow[];
    uploadCompleted: boolean;
}

export default defineComponent({
    name: "ImportExportLibraryModalMetadataTab",

    components: {
        XlsUploadButton,
        LoadingSpinner
    },

    props: {
        metadataSelectionCount: {
            type: Number,
            default: 0
        },
        isExportingMetadata: {
            type: Boolean,
            default: false
        }
    },

    emits: ["exportMetadata", "change"],

    setup() {
        const { editableGroupIds, metadataFieldIdByName, getValidationError, processRow } = useDeliverableImport();

        return { editableGroupIds, metadataFieldIdByName, getValidationError, processRow };
    },

    data(): MetadataTabData {
        return {
            fileName: "",
            hasError: false,
            errorDetails: [],
            rows: [],
            uploadCompleted: false
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        userCanChangeDeliverableReportingLabel() {
            return userCan(USER_ACTIONS.canChangeDeliverableReportingLabel, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanManageMetadata() {
            return userCan(USER_ACTIONS.canManageMetadata, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanImportExportMetadata() {
            return this.userCanChangeDeliverableReportingLabel || this.userCanManageMetadata;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        editableColumnsLabel() {
            if (this.userCanManageMetadata && this.userCanChangeDeliverableReportingLabel) {
                return "metadata and reporting label columns";
            }

            return this.userCanManageMetadata ? "metadata columns" : "reporting label column";
        }
    },

    methods: {
        emitState() {
            const state: MetadataTabState = {
                isReady: this.uploadCompleted && !this.hasError,
                rows: this.rows
            };

            this.$emit("change", state);
        },

        resetUpload() {
            this.uploadCompleted = false;
            this.emitState();
        },

        downloadMetadata() {
            if (this.isExportingMetadata) {
                return;
            }
            this.$emit("exportMetadata");
        },

        onImportError(error: XlsUploadError) {
            this.hasError = true;
            this.errorDetails = [error.message];
            this.emitState();
        },

        async onXlsLoad(importedData: ImportedXlsData) {
            this.uploadCompleted = false;
            this.hasError = false;
            this.errorDetails = [];

            const metadataSheet = importedData[METADATA_SHEET_NAME] as ImportedSheet | undefined;

            if (!importedData.sheets.length || !metadataSheet) {
                this.errorDetails = [
                    "The file is either empty or is incorrectly formatted. Please check your data and try again."
                ];

                this.hasError = true;
                this.emitState();

                return;
            }

            await this.$nextTick();
            this.processMetadataInput(metadataSheet.sheetData);

            this.uploadCompleted = true;
            this.fileName = importedData.fileName;
            this.emitState();
        },

        processMetadataInput(dataArray: ImportRow[]) {
            this.rows = [];
            this.hasError = false;
            this.errorDetails = [];

            const header = (dataArray.shift() ?? []) as string[];

            // Fixed columns are located by header name so the parser stays correct when a
            // permission-omitted column (e.g. reportingLabel) is absent from the sheet.
            const fixedColumnIndexByHeader = Object.fromEntries(
                METADATA_SHEET_FIXED_HEADERS.map(name => [name, header.indexOf(name)])
            ) as Record<MetadataSheetFixedHeader, number>;

            const sizeIndex = fixedColumnIndexByHeader.size;
            const languageIndex = fixedColumnIndexByHeader.language;
            const reportingLabelIndex = fixedColumnIndexByHeader.reportingLabel;

            const fixedColumnIndices = new Set(Object.values(fixedColumnIndexByHeader).filter(index => index >= 0));
            const dynamicColumnIndices = header
                .map((_, index) => index)
                .filter(index => !fixedColumnIndices.has(index));

            const dynamicHeaders = dynamicColumnIndices.map(index => header[index] ?? "");
            const groupNames = dynamicHeaders.slice(0, this.editableGroupIds.length);
            const groupColumnIndices = dynamicColumnIndices.slice(0, this.editableGroupIds.length);
            const metadataFieldNames = dynamicHeaders.slice(this.editableGroupIds.length);
            const metadataColumnIndices = dynamicColumnIndices.slice(this.editableGroupIds.length);

            const rows: MetadataImportRow[] = [];
            dataArray.forEach((row, i) => {
                const rowNumber = i + 3;

                const normalizedRow: ImportRow = [
                    row[sizeIndex] ?? null,
                    row[languageIndex] ?? null,
                    ...groupColumnIndices.map(index => row[index] ?? null)
                ];

                const validationError = this.getValidationError(normalizedRow, groupNames, rowNumber);

                if (validationError.length) {
                    this.hasError = true;
                    this.errorDetails.push(...validationError);
                    return;
                }

                const identifier = this.processRow(normalizedRow, groupNames);

                // Only include categories the user is authorized for so the backend, which
                // authorizes metadata and reporting labels independently, does not reject the import.
                const metadataValues: Record<string, string> = {};
                if (this.userCanManageMetadata) {
                    metadataFieldNames.forEach((name, index) => {
                        const fieldId = this.metadataFieldIdByName[name];
                        const columnIndex = metadataColumnIndices[index];
                        if (fieldId && columnIndex !== undefined) {
                            metadataValues[fieldId] = row[columnIndex] ?? "";
                        }
                    });
                }

                const reportingLabel =
                    this.userCanChangeDeliverableReportingLabel && reportingLabelIndex >= 0
                        ? ((row[reportingLabelIndex] ?? "") as string)
                        : "";

                rows.push({ identifier, metadataValues, reportingLabel });
            });

            this.rows = rows;
            this.emitState();
        }
    }
});
</script>

<style lang="scss" scoped>
.link-like--disabled {
    cursor: default;
    opacity: 0.6;
    pointer-events: none;
}

.importExportModal__download-spinner {
    display: inline-block;
    margin-left: 4px;
    vertical-align: middle;
}
</style>
